import React, { useState, useEffect, useRef } from 'react';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Send, MessageSquare } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Messages() {
  const { user } = useAuth();
  const [trainer, setTrainer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (user) fetchTrainerAndMessages();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchTrainerAndMessages = async () => {
    try {
      const trainerRes = await axios.get(`${API}/trainer/info`);
      setTrainer(trainerRes.data);
      const msgsRes = await axios.get(`${API}/messages/conversation/${user.id}/${trainerRes.data.id}`);
      setMessages(msgsRes.data || []);

      // Poll for new messages every 10s
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/messages/conversation/${user.id}/${trainerRes.data.id}`);
          setMessages(r.data || []);
        } catch { /* polling, ignore */ }
      }, 10000);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !trainer) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        sender_id: user.id,
        sender_name: user.name,
        receiver_id: trainer.id,
        message: newMessage.trim(),
        sender_mode: 'owner'
      });
      setNewMessage('');
      const msgsRes = await axios.get(`${API}/messages/conversation/${user.id}/${trainer.id}`);
      setMessages(msgsRes.data || []);
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-gray-800 flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-edupaws-sage" />
            Messages
          </h1>
          <p className="text-gray-500 font-body mt-1">Chat directly with the Edu Paws team</p>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-3xl border-2 border-gray-100 shadow-lg overflow-hidden flex flex-col" style={{ height: '60vh' }}>
          {/* Trainer header */}
          <div className="bg-edupaws-navy px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">EP</span>
            </div>
            <div>
              <div className="text-white font-heading font-bold" data-testid="trainer-name">Edu Paws Team</div>
              <div className="text-white/60 text-sm font-body">Training Support</div>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50" data-testid="messages-list">
            {messages.length === 0 ? (
              <div className="text-center py-16 text-gray-400 font-body">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No messages yet. Say hi to your trainer!</p>
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.sender_id === user.id;
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div
                      data-testid={`message-${msg.id}`}
                      className={`max-w-[75%] px-4 py-3 rounded-2xl font-body text-sm ${
                        isMe
                          ? 'bg-edupaws-sage text-white rounded-br-md'
                          : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                      }`}
                    >
                      <p>{msg.message}</p>
                      <div className={`text-[10px] mt-1 ${isMe ? 'text-white/60' : 'text-gray-400'}`}>
                        {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="p-4 border-t border-gray-100 bg-white">
            <div className="flex gap-2">
              <Input
                data-testid="message-input"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Type your message..."
                className="flex-1 h-12 rounded-xl"
                disabled={sending}
              />
              <button
                data-testid="send-message-btn"
                onClick={handleSend}
                disabled={sending || !newMessage.trim()}
                className="h-12 px-5 bg-edupaws-sage text-white rounded-xl font-heading font-bold flex items-center gap-2
                  hover:bg-edupaws-sage/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </OwnerLayout>
  );
}
